"use client";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  ReactFlowProvider,
  BackgroundVariant,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useGraphStore } from "@/store/graph-store";
import { CustomNode } from "./nodes/custom-node";
import { CustomEdge } from "./edges/custom-edge";
import {
  fetchNodesAction,
  fetchEdgesAction,
  createNodeAction,
  updateNodeAction,
  deleteNodeAction,
  createEdgeAction,
} from "@/actions/graph-actions";
import { Swimlanes } from "./swimlanes";
import { Maximize2, Trash2, X, FolderGit } from "lucide-react";
import { cn } from "@/lib/utils";

const nodeTypes = {
  custom: CustomNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

function mapDbTypeToCategory(dbType: string): string {
  switch (dbType) {
    case "goal":
      return "strategy";
    case "people":
    case "agent":
      return "people";
    case "project":
    case "milestone":
    case "task":
    case "lead":
      return "work";
    case "invoice":
    case "payment":
      return "finance";
    case "supplier":
    case "asset":
      return "operations";
    case "risk":
      return "risk";
    case "documents":
      return "documents";
    case "initiative":
      return "innovation";
    default:
      return dbType;
  }
}

function FlowCanvas() {
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    setNodes,
    setEdges,
    setSelectedNodeId,
    setLoading,
    isLoading,
    allNodes,
    applyCategoryFilter,
    selectedNodeId,
  } = useGraphStore();

  const { screenToFlowPosition, fitView } = useReactFlow();

  const searchParams = useSearchParams();
  const queryNodeId = searchParams.get("nodeId");

  useEffect(() => {
    if (queryNodeId && nodes.length > 0) {
      const exists = nodes.some((n) => n.id === queryNodeId);
      if (exists) {
        setSelectedNodeId(queryNodeId);
      }
    }
  }, [queryNodeId, nodes, setSelectedNodeId]);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string;
  } | null>(null);

  // Load nodes and edges on mount
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const dbNodes = await fetchNodesAction();
        const dbEdges = await fetchEdgesAction();

        // Convert DB format to React Flow format
        const nodeCount = dbNodes.length;
        const spacing = 320; // 224px width + 96px gap
        const circumference = nodeCount * spacing;
        const radius = Math.max(350, circumference / (2 * Math.PI));

        const centerX = radius + 100;
        const centerY = radius + 100;

        const rfNodes = dbNodes.map((n, idx) => {
          const angle = (idx / nodeCount) * 2 * Math.PI;
          return {
            id: n.id,
            type: "custom",
            // Calculate positions if not provided (mock circle layout)
            position: (n.dataPayload as any)?.position || {
              x: centerX + Math.cos(angle) * radius,
              y: centerY + Math.sin(angle) * radius,
            },
            data: {
              label: n.name,
              type: mapDbTypeToCategory(n.type),
              status: (n.details as any)?.status || "healthy",
              subtext:
                n.type === "strategy" || n.type === "goal"
                  ? `Progress: ${(n.details as any)?.progress || 0}%`
                  : undefined,
            },
          };
        });

        const rfEdges = dbEdges.map((e) => ({
          id: e.id,
          source: e.sourceId,
          target: e.targetId,
          type: "custom",
          data: {
            type: e.type,
          },
        }));

        setNodes(rfNodes);
        setEdges(rfEdges);

        // Apply default clean view (not all categories to prevent cluttered look)
        applyCategoryFilter(["strategy", "people", "risk"]);

        // Auto-center and zoom to fit loaded nodes on initial render
        setTimeout(() => {
          fitView({ padding: 0.2, duration: 800 });
        }, 100);
      } catch (err) {
        console.error("Failed to load graph data:", err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [setNodes, setEdges, setLoading, applyCategoryFilter, fitView]);

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const onDrop = async (event: React.DragEvent) => {
    event.preventDefault();

    const rawData = event.dataTransfer.getData("application/reactflow");
    if (!rawData) return;

    try {
      const dragData = JSON.parse(rawData);
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeId = dragData.isTemplate
        ? `${dragData.type}_${Date.now().toString().slice(-6)}`
        : dragData.existingId;

      if (dragData.isTemplate) {
        // Create new node in DB via Server Action
        const input = {
          id: nodeId,
          type: dragData.type,
          name: dragData.label,
          dataPayload: { position },
        };

        await createNodeAction(input);

        // Add to Zustand store
        const newNode = {
          id: nodeId,
          type: "custom",
          position,
          data: {
            label: dragData.label,
            type: mapDbTypeToCategory(dragData.type),
            status: "healthy",
          },
        };
        setNodes([...nodes, newNode]);
      } else {
        // Drop existing node: update position in DB and add to canvas
        const input = {
          id: nodeId,
          type: dragData.type,
          name: dragData.label,
          dataPayload: { position },
        };
        await updateNodeAction(input);

        const newNode = {
          id: nodeId,
          type: "custom",
          position,
          data: {
            label: dragData.label,
            type: mapDbTypeToCategory(dragData.type),
            status: "healthy",
          },
        };
        setNodes([...nodes, newNode]);
      }
    } catch (err) {
      console.error("Failed to drop element on canvas:", err);
    }
  };

  const onConnectHandler = async (connection: any) => {
    // Force type to 'custom' so it renders using our custom edge component (CustomEdge)
    const customConnection = { ...connection, type: "custom" };
    onConnect(customConnection);

    const sourceNode = nodes.find((n) => n.id === connection.source);
    const targetNode = nodes.find((n) => n.id === connection.target);
    const sourceType = (sourceNode?.data?.type as string) || "work";
    const targetType = (targetNode?.data?.type as string) || "work";

    let type: "strategic" | "financial" | "operational" | "risk" | "info" =
      "info";
    if (sourceType === "strategy" || targetType === "strategy")
      type = "strategic";
    else if (sourceType === "finance" || targetType === "finance")
      type = "financial";
    else if (sourceType === "risk" || targetType === "risk") type = "risk";
    else if (sourceType === "work" && targetType === "work")
      type = "operational";

    const edgeId = `edge_${Date.now()}`;

    try {
      await createEdgeAction({
        id: edgeId,
        sourceId: connection.source,
        targetId: connection.target,
        sourceType,
        targetType,
        type,
        metadata: {},
      });
    } catch (err) {
      console.error("Failed to save edge in DB:", err);
    }
  };

  const containerRef = React.useRef<HTMLDivElement>(null);

  const onNodeContextMenu = (event: React.MouseEvent, node: any) => {
    event.preventDefault();
    if (containerRef.current) {
      const bounds = containerRef.current.getBoundingClientRect();
      setContextMenu({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
        nodeId: node.id,
      });
    }
  };

  const handlePaneClick = () => {
    setSelectedNodeId(null);
    setContextMenu(null);
  };

  return (
    <div
      ref={containerRef}
      className="h-full w-full bg-background relative select-none"
    >
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-xs flex items-center justify-center z-50">
          <div className="text-sm font-medium text-text-secondary animate-pulse">
            Caricamento del Grafo...
          </div>
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnectHandler}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={(_, node) => {
          setSelectedNodeId(node.id);
          setContextMenu(null);
        }}
        onNodeContextMenu={onNodeContextMenu}
        onPaneClick={handlePaneClick}
        onDragOver={onDragOver}
        onDrop={onDrop}
        fitView
      >
        <Swimlanes />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        <Controls className="!bg-card !border-border !text-foreground rounded-lg overflow-hidden shadow-md" />
        <MiniMap
          nodeColor={() => "var(--border-subtle)"}
          maskColor="rgba(0, 0, 0, 0.05)"
          className={cn(
            "!bg-card !border-border rounded-lg overflow-hidden shadow-md transition-all duration-300",
            selectedNodeId ? "!right-[340px]" : "!right-4",
          )}
        />
      </ReactFlow>

      {contextMenu && (
        <div
          className="absolute z-50 bg-card border border-border shadow-lg rounded-xl py-1.5 w-52 text-sm font-semibold text-text-primary"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              setSelectedNodeId(contextMenu.nodeId);
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-card-hover flex items-center gap-2.5 cursor-pointer transition-colors"
          >
            <Maximize2 className="h-4 w-4 text-text-secondary" />
            Visualizza Dettagli
          </button>
          <button
            onClick={() => {
              setNodes(nodes.filter((n) => n.id !== contextMenu.nodeId));
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-card-hover flex items-center gap-2.5 cursor-pointer transition-colors text-text-secondary"
          >
            <X className="h-4 w-4" />
            Nascondi
          </button>
          <button
            onClick={() => {
              const connectedEdges = edges.filter(
                (e) =>
                  e.source === contextMenu.nodeId ||
                  e.target === contextMenu.nodeId,
              );
              const connectedIds = connectedEdges.map((e) =>
                e.source === contextMenu.nodeId ? e.target : e.source,
              );
              const toAdd = allNodes.filter(
                (n) =>
                  connectedIds.includes(n.id) &&
                  !nodes.some((existing) => existing.id === n.id),
              );
              if (toAdd.length > 0) {
                setNodes([...nodes, ...toAdd]);
              }
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-card-hover flex items-center gap-2.5 cursor-pointer transition-colors text-primary-600"
          >
            <FolderGit className="h-4 w-4" />
            Espandi Relazioni
          </button>
          <hr className="border-border/50 my-1" />
          <button
            onClick={async () => {
              if (
                confirm(
                  "Sei sicuro di voler eliminare questo nodo dal database?",
                )
              ) {
                await deleteNodeAction(contextMenu.nodeId);
                setNodes(nodes.filter((n) => n.id !== contextMenu.nodeId));
                setEdges(
                  edges.filter(
                    (e) =>
                      e.source !== contextMenu.nodeId &&
                      e.target !== contextMenu.nodeId,
                  ),
                );
                setSelectedNodeId(null);
              }
              setContextMenu(null);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-error-soft text-error flex items-center gap-2.5 cursor-pointer transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Elimina
          </button>
        </div>
      )}
    </div>
  );
}

export function GraphCanvas() {
  return (
    <ReactFlowProvider>
      <FlowCanvas />
    </ReactFlowProvider>
  );
}
